import {
  isTemplateHideOperation,
  type TemplateInOut,
} from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import type { ModelMetaType } from '../../types/metaModel'
import type { TemplateVersionInfo } from '../../types/templateModel'
import {
  enrichTemplate,
  templateModelTypeFromCreateOperation,
} from '../helpers'
import { flattenHierarchy } from '../transformers/templateFlattening'
import { useTemplateHierarchy } from '../versionInfo/useTemplateHierarchy'
import {
  assertTemplateCreateOperation,
  type EnrichedTemplateCreateOperationWithVersionInfo,
} from './types'
import { useTemplateOperationsWithVersionInfo } from './useTemplateOperationsWithVersionInfo'

/**
 * Hook for returning the template create operation where value setups are enriched
 * to contain the template version or draft from where the value has been set.
 *
 * This hook will also perform client side flattening if the isFlattened parameter is true.
 */
export const useEnrichedTemplateCreateOperation = (
  versionInfo: Optional<TemplateVersionInfo>,
  isFlattened?: boolean,
): {
  enrichedTemplateCreationOperation?: Optional<EnrichedTemplateCreateOperationWithVersionInfo>
  originalTemplate?: Optional<TemplateInOut>
  isFlattened?: boolean
} => {
  const hierarchy = useTemplateHierarchy({
    forTemplate: versionInfo,
    withReplacements: true,
  })

  const areVersionsMissingOrHidden = hierarchy.some(
    (checkedVersion) =>
      checkedVersion.isMissing || checkedVersion.isHideVersion,
  )
  const metaModel = useLazyLayerMetaModel(versionInfo?.layer.layerId)
  const hierarchyOperations = useTemplateOperationsWithVersionInfo(hierarchy)

  return useMemo(() => {
    if (!!hierarchyOperations?.length && versionInfo) {
      const currentTemplateOperation =
        hierarchyOperations[hierarchyOperations.length - 1]

      if (isTemplateHideOperation(currentTemplateOperation)) {
        return {
          enrichedTemplateCreationOperation: undefined,
          originalTemplate: undefined,
          isFlattened: false,
        }
      } else if (!isFlattened || areVersionsMissingOrHidden) {
        return {
          enrichedTemplateCreationOperation: currentTemplateOperation?.entity
            ? {
                ...currentTemplateOperation,
                entity: enrichTemplate({
                  template: currentTemplateOperation.entity,
                  metaModel,
                  path: `{${currentTemplateOperation.entity.name}}`,
                }),
              }
            : null,
          originalTemplate: currentTemplateOperation?.entity,
          isFlattened: false,
        }
      } else {
        const enrichedHierarchyOperations = hierarchyOperations
          .map((operation) => {
            assertTemplateCreateOperation(operation)
            assertNotEmpty(operation?.entity)

            return enrichTemplate({
              template: operation.entity,
              metaModel,
              path: `{${operation.entity.name}}`,
            })
          })
          .filter(notEmpty)
        const leafCreateOperation =
          hierarchyOperations[hierarchyOperations.length - 1]

        assertNotEmpty(leafCreateOperation)

        const modelTypeName =
          templateModelTypeFromCreateOperation(leafCreateOperation)

        const modelType = modelTypeName
          ? (metaModel.types.get(modelTypeName) as Optional<ModelMetaType>)
          : undefined
        const isMappingsTemplate =
          !!modelType?.templateType?.stereoTypes.includes('MappingTemplate')

        const flattenedTemplate = flattenHierarchy(
          enrichedHierarchyOperations,
          isMappingsTemplate,
        )

        assertNotEmpty(currentTemplateOperation?.entity)

        return {
          enrichedTemplateCreationOperation: {
            ...leafCreateOperation,
            entity: flattenedTemplate,
          },
          originalTemplate: currentTemplateOperation.entity,
          isFlattened: true,
        }
      }
    }

    return {}
  }, [
    areVersionsMissingOrHidden,
    hierarchyOperations,
    isFlattened,
    metaModel,
    versionInfo,
  ])
}
